import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  addDateValidation,
  Button,
  Loader,
  useDialog,
  useSnackbar,
} from '@fdha/web-ui-library';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import {
  FieldArray,
  Form,
  Formik,
  FormikState,
  FastField,
  FieldInputProps,
  FormikProps,
} from 'formik';
import { omit } from 'lodash';
import Icon from 'react-eva-icons';
import {
  NoteQuestion,
  useAddChartMutation,
  useDeleteChartMutation,
  useUpdateChartMutation,
  FormBuilderQuestionType,
  DateQuestionInitialState,
  DateQuestion,
} from '@fdha/graphql-api-admin';
import { convertQuestionGroupedPropsToQuestionSplittedProps } from '@fdha/common-utils';
import { isValid } from 'date-fns';
import * as Yup from 'yup';
import { useReactiveVar } from '@apollo/client';

import {
  editNote,
  hasDraftNote,
  NotesProps,
  removeDraftNote,
  removeEditNote,
  editNotes,
} from '../../../../states/notesState';
import { useLocalStorage } from '../../../../hooks';
import { createAutoSaveTimer } from '../../../../utils';

import ChartQuestionField from './ChartQuestionField';
import Card from './Card';
import { buildListNotesRefetch } from './constants';

const validationSchema = Yup.object().shape({
  questions: Yup.array()
    .nullable()
    .of(
      Yup.object().shape({
        answer: Yup.array()
          .nullable()
          .when(['type'], {
            is: (type: FormBuilderQuestionType) =>
              type === FormBuilderQuestionType.Date,
            then: Yup.array()
              .nullable()
              .of(addDateValidation(undefined, undefined, false, true)),
          }),
      })
    ),
});

export interface ChartForm {
  name: string;
  questions: NoteQuestion[];
}

interface Props {
  chart: NotesProps;
  patientId: string;
  preview?: boolean;
  index?: number;
  isDraft?: boolean;
}

const ChartCard = ({
  chart,
  patientId,
  preview = false,
  index = 0,
  isDraft = false,
}: Props) => {
  const editNotesState = useReactiveVar(editNotes);

  const isBeingEdited = useMemo(() => {
    return chart.id
      ? editNotesState.some(
          (note) => note.id === chart.id && note.patientId === patientId
        )
      : chart.id === ''; // New chart (draft)
  }, [chart.id, patientId, editNotesState]);

  const isPreviewMode = preview || !isBeingEdited;

  const initialValues: ChartForm = useMemo(() => {
    const questions = preview
      ? chart.questions
      : chart?.questions?.map((question) => {
          let answer = question.answer;

          if (question.type === FormBuilderQuestionType.Date) {
            if (question.answer?.[0] != null) {
              // Date saved may be a string "Invalid Date"
              if (!isValid(new Date(question.answer[0]))) {
                answer = [''];
              }
            } else {
              const customProps = question.customProps as
                | DateQuestion
                | undefined;

              if (
                customProps?.initialState ===
                DateQuestionInitialState.PreloadedWithCurrentDate
              ) {
                const initialValue = new Date().toISOString();

                answer = [initialValue];
              }
            }
          }

          question = { ...question, answer };
          return question;
        });

    const values = {
      name: chart.name ?? '',
      questions: questions ?? [],
    };
    return values;
  }, [preview, chart.questions, chart.name]);

  const { showSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();
  const { saveToLocalStorage, removeFromLocalStorage } =
    useLocalStorage('notes');

  const [deleteNote] = useDeleteChartMutation();
  const [updateNote] = useUpdateChartMutation();
  const [addNote] = useAddChartMutation();

  const saveDraft = useCallback(
    async (values: ChartForm) => {
      const data = {
        [patientId]: {
          index,
          ...chart,
          ...values,
        },
      };

      await saveToLocalStorage(data);
      showSnackbar({
        message: 'Draft saved',
        severity: 'info',
      });
    },
    [index, chart, patientId, saveToLocalStorage, showSnackbar]
  );

  // Store current form values in ref for auto-save interval
  const valuesRef = useRef(initialValues);

  // Store saveDraft in ref so timer doesn't need to be recreated when it changes
  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;

  // Create auto-save timer instance (only once)
  const autoSaveTimer = useMemo(
    () =>
      createAutoSaveTimer({
        onSave: () => {
          const currentValues = valuesRef.current;

          saveDraftRef.current(currentValues);
        },
        interval: 10000,
      }),
    [] // Empty deps - timer instance never recreated
  );

  // Start timer when entering edit mode, stop when exiting or unmounting
  useEffect(() => {
    if (!isPreviewMode) {
      autoSaveTimer.startTimer();
      return () => {
        autoSaveTimer.stopTimer();
      };
    }
  }, [isPreviewMode, autoSaveTimer]);

  const showErrorSnackbar = (message: string) => {
    showSnackbar({ message: message, severity: 'error' });
  };

  const showSuccessSnackbar = (message: string) => {
    showSnackbar({ message: message, severity: 'success' });
  };

  const handleCancel = (
    resetForm: (nextState?: Partial<FormikState<ChartForm>> | undefined) => void
  ) => {
    autoSaveTimer.stopTimer();
    if (chart.id) {
      removeEditNote(chart);
    } else {
      removeDraftNote(chart);
    }
    resetForm({ values: initialValues });
    removeFromLocalStorage(patientId);
    showSnackbar({ message: 'Changes not saved', severity: 'warning' });
  };

  const handleDelete = async () => {
    if (chart.id) {
      openDialog({
        title:
          'Are you sure you want to delete this note? You will not be able to recover it.',
        content: null,
        confirmButtonLabel: 'Delete',
        cancelButtonLabel: 'Keep',
        handleConfirm: async () => {
          try {
            autoSaveTimer.stopTimer();
            removeEditNote(chart);
            await deleteNote({
              variables: { id: chart.id },
              refetchQueries: [buildListNotesRefetch(patientId)],
            });
            removeFromLocalStorage(patientId);
            showSuccessSnackbar('Note deleted');
          } catch (error) {
            autoSaveTimer.startTimer();
            showErrorSnackbar('Error deleting note');
          } finally {
            closeDialog();
          }
        },
      });
    } else {
      autoSaveTimer.stopTimer();
      removeFromLocalStorage(patientId);
      removeDraftNote(chart);
    }
  };

  const handleEdit = () => {
    if (hasDraftNote(patientId)) {
      return showSnackbar({
        message: `There are notes that haven't been saved yet`,
        severity: 'error',
      });
    }

    editNote(chart.id, patientId);
  };

  const handleSubmit = async (values: ChartForm) => {
    const questions = values.questions.map((question) => ({
      ...omit(
        convertQuestionGroupedPropsToQuestionSplittedProps(question),
        'id'
      ),
      answer: question.answer,
    }));

    try {
      autoSaveTimer.stopTimer();
      await saveDraft(values);
      if (chart.id) {
        await updateNote({
          variables: {
            id: chart.id,
            chart: {
              name: values.name,
              questions,
            },
          },
          refetchQueries: [buildListNotesRefetch(patientId)],
          awaitRefetchQueries: true,
        });
        removeEditNote(chart);
      } else {
        await addNote({
          variables: { chart: { ...values, questions }, patientId },
          refetchQueries: [buildListNotesRefetch(patientId)],
          awaitRefetchQueries: true,
        });
        removeDraftNote(chart);
      }
      removeFromLocalStorage(patientId);
      showSuccessSnackbar('Note saved!');
    } catch (error) {
      autoSaveTimer.startTimer();
      console.error(error);
      showErrorSnackbar('Error editing/saving note');
    }
  };

  return (
    <Paper data-testid={`CHART_CARD_${chart.id}`}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnBlur={true}
        validateOnChange={false}
      >
        {({ values, resetForm, isSubmitting }) => {
          // Update ref with current form values
          valuesRef.current = values;

          if (isSubmitting && !isPreviewMode) {
            return (
              <Box
                position="relative"
                minHeight={200}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                padding={4}
              >
                <Loader />
                <Typography variant="body2" color="textSecondary" marginTop={2}>
                  Saving chart...
                </Typography>
              </Box>
            );
          }

          const questionsContent = (
            <FieldArray name="questions">
              {() =>
                values.questions.map((question, idx) => (
                  <FastField
                    key={`${question.id}-${isPreviewMode}`}
                    name={`questions.${idx}.answer`}
                  >
                    {({
                      field,
                      form,
                    }: {
                      field: FieldInputProps<string[]>;
                      form: FormikProps<ChartForm>;
                    }) => (
                      <ChartQuestionField
                        question={question}
                        index={idx}
                        preview={isPreviewMode}
                        field={field}
                        form={form}
                      />
                    )}
                  </FastField>
                ))
              }
            </FieldArray>
          );
          return (
            <Form data-testid={`CHART_CARD_FORM_${chart.name}`}>
              <Card
                note={chart}
                patientId={patientId}
                canFlag={false}
                isDraft={isDraft}
              >
                <Stack spacing={8} margin={2}>
                  {questionsContent}
                </Stack>
              </Card>
              <Box
                display="flex"
                justifyContent={isPreviewMode ? 'flex-end' : 'space-between'}
                alignItems="center"
                padding={2}
                paddingTop={0}
                data-testid={`${isPreviewMode ? 'PREVIEW' : 'EDIT'}_CARD`}
              >
                {!isPreviewMode && (
                  <>
                    <IconButton
                      onClick={handleDelete}
                      data-testid="DELETE_CHART_ICON"
                    >
                      <Icon
                        name="trash-2-outline"
                        size="large"
                        fill={grey[600]}
                      />
                    </IconButton>
                    <Box>
                      <Button
                        onClick={handleCancel.bind(null, resetForm)}
                        data-testid="CANCEL_CHART_BUTTON"
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={isSubmitting}
                        type="submit"
                        variant="contained"
                        color="secondary"
                      >
                        Save
                      </Button>
                    </Box>
                  </>
                )}
                {isPreviewMode && (
                  <Box display="flex" justifyContent="flex-end">
                    <IconButton
                      onClick={handleEdit}
                      data-testid="EDIT_CHART_ICON"
                    >
                      <Icon name="edit-outline" size="large" fill={grey[600]} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Paper>
  );
};

export default ChartCard;
